/**
 * Turning the export screen's toggles into a request.
 *
 * Kept free of imports on purpose — the screen and the API client both pull in
 * `react-native`, so this is the only part of the path a plain Node test can
 * load. It is also the part where a mistake is invisible: asking the server for
 * the wrong sections produces a perfectly well-formed export with silently
 * missing numbers.
 */

/** Which slice of history to export. */
export type ExportRange = "last30" | "last12months" | "all";

/** Which parts the server should build. Omitted parts are never queried. */
export type ExportSection = "growthStats" | "weeklySummaries" | "reflections";

/** The screen's four toggles. `privateNotes` is not a section of its own. */
export type ExportSectionToggles = {
  growthStats: boolean;
  weeklySummaries: boolean;
  reflections: boolean;
  privateNotes: boolean;
};

export type ReflectionExportOptions = {
  range?: ExportRange;
  sections?: ExportSection[];
  /** Leave the free-text notes out of the response entirely. */
  includeNotes?: boolean;
};

/**
 * Which sections the server has to build for these toggles.
 *
 * `growthStats` needs the reflections too, even when the person left the
 * journal itself out of the file: the summary's reflection count and its
 * "days showed up" figure are both derived from the list. Asking for exactly
 * the toggles that are on would report both as zero.
 */
export function serverSectionsFor(
  toggles: ExportSectionToggles,
): ExportSection[] {
  const wanted: ExportSection[] = [];
  if (toggles.growthStats) wanted.push("growthStats");
  if (toggles.weeklySummaries) wanted.push("weeklySummaries");
  if (toggles.reflections || toggles.growthStats) wanted.push("reflections");
  return wanted;
}

/**
 * Whether the notes should leave the server at all.
 *
 * Not simply `privateNotes`. The four toggles are independent checkboxes, so
 * "growth stats, no journal, private notes on" is reachable — and in that state
 * `serverSectionsFor` asks for the reflections anyway, because the summary's
 * counts are derived from them. Following `privateNotes` alone would then fetch
 * every note for a file that contains no journal at all.
 *
 * The notes are wanted only when the journal itself is being written out.
 */
export function includeNotesFor(toggles: ExportSectionToggles): boolean {
  return toggles.privateNotes && toggles.reflections;
}

/**
 * The whole request for a set of toggles.
 *
 * Sections and notes are decided together on purpose: they are coupled through
 * the reflections list, which can be requested for a reason other than putting
 * it in the file. Deciding them in two places is how the notes leaked.
 */
export function exportRequestFor(
  range: ExportRange,
  toggles: ExportSectionToggles,
): ReflectionExportOptions {
  return {
    range,
    sections: serverSectionsFor(toggles),
    includeNotes: includeNotesFor(toggles),
  };
}

/**
 * Only sends what was actually chosen. An omitted parameter means "everything"
 * on the server, so a request with no options is the one older clients made.
 */
export function exportQuery(options: ReflectionExportOptions = {}): string {
  const params = new URLSearchParams();
  if (options.range) params.set("range", options.range);
  if (options.sections?.length) {
    params.set("sections", options.sections.join(","));
  }
  // Only the negative is sent: `notes=true` is the default, and sending it
  // would make every request carry a parameter that changes nothing.
  if (options.includeNotes === false) params.set("notes", "false");
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}
